import type { SessionUserDto } from '@betreuung/shared';
import { WifiOff } from 'lucide-react';
import { useSseSync, useOnlineStatus } from '@/api/sse';
import { AssignmentFlowsHost } from '@/features/assignment/AssignmentFlowsHost';
import { AssignmentFlowsProvider } from '@/features/assignment/useAssignmentFlows';
import { AdminScreen } from '@/features/admin/AdminScreen';
import { ChangesTab } from '@/features/changes/ChangesTab';
import { CurrentTab } from '@/features/current/CurrentTab';
import { MonthTab } from '@/features/month/MonthTab';
import { StatsTab } from '@/features/stats/StatsTab';
import { YearTab } from '@/features/year/YearTab';
import { de } from '@/i18n/de';
import { AppDataProvider } from './appData';
import { Bottombar } from './Bottombar';
import { Sidebar } from './Sidebar';
import { useNavigation, useVisibleTabs } from './navigation';
import { useIsDesktop } from './useMediaQuery';

/**
 * Application frame.
 *
 * Every tab this account has stays mounted and is hidden rather than unmounted:
 * the lists in
 * "Aktuell" and "Änderungen" are infinite-scrolled, and losing their scroll
 * position on every tab switch would be the most noticeable regression possible.
 * A tab that is not rendered is also not fetching.
 */
export function AppShell({ user }: { user: SessionUserDto }) {
  return (
    <AppDataProvider user={user}>
      <AssignmentFlowsProvider>
        <ShellLayout />
        <AssignmentFlowsHost />
      </AssignmentFlowsProvider>
    </AppDataProvider>
  );
}

const TAB_CONTENT = {
  current: CurrentTab,
  month: MonthTab,
  year: YearTab,
  stats: StatsTab,
  changes: ChangesTab,
} as const;

function ShellLayout() {
  const { adminOpen } = useNavigation();
  const { tabs, activeTab } = useVisibleTabs();
  const isDesktop = useIsDesktop();
  const syncStatus = useSseSync(true);
  const online = useOnlineStatus();

  const disconnected = !online || syncStatus === 'offline';

  return (
    // A fixed viewport height, not a minimum: the navigation has to stay put
    // while only the content scrolls, and `flex-1` on <main> can only resolve to
    // "the space left over" if its container cannot grow with the content.
    // `dvh` follows the mobile browser's collapsing toolbar.
    <div className="flex h-dvh overflow-hidden bg-surface">
      {isDesktop ? <Sidebar /> : null}

      <div className="flex h-full min-w-0 flex-1 flex-col">
        {disconnected ? (
          <div
            role="status"
            className="flex items-center justify-center gap-2 bg-cal-9 px-4 py-1.5 text-hint font-medium text-cal-9-fg"
          >
            <WifiOff className="size-3.5" />
            {de.errors.offline}
          </div>
        ) : null}

        <main className="min-h-0 flex-1">
          {tabs.map((value) => {
            const Content = TAB_CONTENT[value];
            return (
              // Each tab scrolls in its own container, which is what actually
              // preserves its scroll position across tab switches — a shared
              // scroller would carry one tab's offset into the next and clamp it.
              <div key={value} hidden={value !== activeTab} className="h-full overflow-y-auto">
                <Content />
              </div>
            );
          })}
        </main>

        {isDesktop ? null : <Bottombar />}
      </div>

      {adminOpen ? <AdminScreen /> : null}
    </div>
  );
}
